import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  selectCurrentStudioScene,
  selectStudioImages,
  saveScene,
  StudioImage,
  StudioService,
  StudioAnimationService,
} from '../../element-editor/store/studio';
import {
  StudioScene,
  StudioSceneCell,
  StudioSceneBackgroundDefinition,
} from '../../element-editor/store/studio/studio.types';
import { cloneDeep } from 'lodash';
import { StudioLayoutService } from '../../element-editor/store/studio/layout';
import { CanvasService } from '../../services/canvas/canvas.service';

interface LayoutCellPreview {
  type: 'text' | 'image' | 'video';
  x: number;
  y: number;
  w: number;
  h: number;
  aspectRatio: number; // width / height ratio for the content
  bg?: {
    type: 'color' | 'image' | 'video';
    color?: string;
    image?: string;
    video?: string;
  };
}

@Component({
  selector: 'nw-layout-settings',
  templateUrl: './layout-settings.component.html',
  styleUrls: ['./layout-settings.component.scss'],
})
export class LayoutSettingsComponent implements OnDestroy {
  cells: LayoutCellPreview[] = [];
  currentScene: StudioScene | undefined;
  private images: Record<string, StudioImage> = {};
  private destroy$ = new Subject<void>();
  private subs = new Subscription();
  previewMode: 'layout' | 'animation' | 'background';
  cellsByKey: Record<string, LayoutCellPreview[]> = {
    title: [
      {
        type: 'text',
        w: 0.5,
        h: 0.2,
        x: 0.25,
        y: 0.4,
        aspectRatio: 5 / 2,
      },
    ],
    titleSubtitle: [
      {
        type: 'image',
        w: 1.3,
        h: 1.3,
        x: 0,
        y: -0.2,
        aspectRatio: 5 / 2,
        bg: {
          type: 'color',
          color: '#adadad',
        },
      },
      {
        type: 'text',
        w: 0.8,
        h: 0.3,
        x: 0.2,
        y: 0.35,
        aspectRatio: 5 / 2,
      },
      {
        type: 'text',
        w: 1,
        h: 0.3,
        x: 0.1,
        y: 0.4,
        aspectRatio: 5 / 2,
      },
    ],
    titleWithBg: [
      {
        type: 'image',
        w: 1,
        h: 1,
        x: 0,
        y: 0,
        aspectRatio: 5 / 2,
        bg: {
          type: 'color',
          color: '#adadad',
        },
      },
      {
        type: 'text',
        w: 0.8,
        h: 0.3,
        x: 0.1,
        y: 0.12,
        aspectRatio: 5 / 2,
      },
      {
        type: 'text',
        w: 1,
        h: 0.3,
        x: 0,
        y: 0.4,
        aspectRatio: 5 / 2,
      },
      {
        type: 'text',
        w: 1,
        h: 0.2,
        x: 0,
        y: 0.7,
        aspectRatio: 5 / 2,
      },
    ],
    titleSubtitleWithBg: [
      {
        type: 'image',
        w: 1,
        h: 1,
        x: 0,
        y: 0,
        aspectRatio: 5 / 2,
        bg: {
          type: 'color',
          color: '#adadad',
        },
      },
      {
        type: 'text',
        w: 1,
        h: 0.3,
        x: 0,
        y: 0.12,
        aspectRatio: 5 / 2,
      },
      {
        type: 'text',
        w: 0.5,
        h: 0.2,
        x: 0.25,
        y: 0.7,
        aspectRatio: 5 / 2,
      },
    ],
  };

  templateKeys: Array<keyof typeof this.cellsByKey> = [
    'title',
    'titleSubtitle',
    'titleWithBg',
    'titleSubtitleWithBg',
  ];

  get currentStudioSceneShape() {
    return this.studioService.currentSceneShape;
  }
  constructor(
    private studioService: StudioService,
    private store: Store,
    private layoutService: StudioLayoutService,
    private studioAnimationService: StudioAnimationService,
    private cs: CanvasService,
  ) {
    this.store
      .select(selectCurrentStudioScene)
      .pipe(takeUntil(this.destroy$))
      .subscribe(scene => {
        this.currentScene = cloneDeep(scene);
        this.updateCellPreviews();
      });

    this.store
      .select(selectStudioImages)
      .pipe(takeUntil(this.destroy$))
      .subscribe(images => {
        this.images = images;
        this.updateCellPreviews();
      });

    this.subs.add(
      this.cs.keyEventSubscribe('a', consume => {
        this.studioAnimationService.applyRandomAnimation();
        consume();
      }),
    );

    this.subs.add(
      this.cs.keyEventSubscribe(
        'Shift+a',
        consume => {
          this.openLayoutModal('animation');
          consume();
        },
        -10,
      ),
    );
  }

  private updateCellPreviews() {
    if (!this.currentScene?.cells) {
      this.cells = [];
      return;
    }

    return;
    // Map scene cells to preview format
    this.cells = Object.entries(this.currentScene.cells).map(([key, cell]) => {
      const type = this.getCellType(key, cell);
      const preview: LayoutCellPreview = {
        type,
        x: cell.x,
        y: cell.y,
        w: cell.w,
        h: cell.h,
        aspectRatio: this.getAspectRatio(type, cell),
      };

      // Add background definition if fill color is present
      if (cell.fill) {
        preview.bg = {
          type: 'color',
          color: cell.fill,
        };
      }

      // Add background definition if cell has an image as background
      if (cell.image && type !== 'image') {
        preview.bg = {
          type: 'image',
          image: cell.image,
        };
      }

      // Add background definition if cell has a video as background
      if (cell.videoInstance && type !== 'video') {
        // preview.bg = {
        //   type: 'video',
        //   video: cell.video.id,
        // };
      }

      return preview;
    });
  }

  regenerateLayout() {
    this.layoutService.regenerateLayout();
  }
  regenerateAnimation() {
    this.studioAnimationService.applyRandomAnimation();
  }

  regenerateBackground() {
    this.studioService.regenerateBackground();
  }

  private getCellType(
    key: string,
    cell: StudioSceneCell,
  ): 'text' | 'image' | 'video' {
    if (key.startsWith('text:') || cell.text) return 'text';
    if (key.startsWith('image:') || cell.image) return 'image';
    if (key.startsWith('video:') || cell.videoInstance) return 'video';
    return 'text';
  }

  private getAspectRatio(
    type: 'text' | 'image' | 'video',
    cell: StudioSceneCell,
  ): number {
    if (type === 'text') {
      return 5 / 2; // 5:2 ratio for text
    }
    if (type === 'image' && cell.image) {
      const image = this.images[cell.image];
      if (image?.width && image?.height) {
        return image.width / image.height;
      }
    }
    // Default to 16:9 for video or unknown
    return 16 / 9;
  }

  selectTemplate(templateKey: keyof typeof this.cellsByKey) {
    this.cells = this.cellsByKey[templateKey];

    // Convert template cells to StudioSceneCell format
    const cells: Record<string, StudioSceneCell> = {};

    const indexesByCellType = {
      text: 0,
      image: 0,
      video: 0,
    };

    this.cellsByKey[templateKey].forEach(cell => {
      const index = indexesByCellType[cell.type];
      const cellId = `${cell.type}:${index}`;
      // -- //
      const studioCell: StudioSceneCell = {
        x: cell.x,
        y: cell.y,
        w: cell.w,
        h: cell.h,
      };

      // Add background if present //
      if (cell.type === 'image') {
        studioCell.image = this.currentScene?.images[index];
      }
      // -- //
      if (cell.type === 'video') {
        studioCell.videoInstance = this.currentScene?.videoInstances[index];
      }
      // if (cell.bg) {
      //   if (cell.bg.type === 'color') {
      //     studioCell.fill = cell.bg.color;
      //   } else if (cell.bg.type === 'image') {
      //     studioCell.image = this.currentScene?.images[index];
      //   } else if (cell.bg.type === 'video') {
      //     studioCell.video = cell.bg.video;
      //   }
      // }

      // Add text placeholder if it's a text cell without defined text
      if (cell.type === 'text') {
        studioCell.text = this.currentScene?.texts[index] || 'Text';
        if (!this.currentScene?.texts[index]) {
          this.currentScene.texts[index] = 'Text';
          this.store.dispatch(
            saveScene({
              sceneId: this.currentScene.id,
              value: cloneDeep(this.currentScene),
            }),
          );
        }
      }

      cells[cellId] = studioCell;
      indexesByCellType[cell.type]++;
    });

    // Dispatch action to save the layout
    // this.store.dispatch(saveCurrentLayout({ cells }));

    console.log('Selected template:', templateKey, 'Cells:', cells);
  }

  openLayoutModal(type: 'layout' | 'animation' | 'background') {
    this.previewMode = type;
  }

  closeModal() {
    this.previewMode = null;
  }

  onBgChange(def: StudioSceneBackgroundDefinition) {
    const sceneShape = this.studioService.currentSceneShape;
    if (!sceneShape) return;
    sceneShape.scene.background = def;
    sceneShape.initBackground(def);
    sceneShape.save();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.unsubscribe();
  }
}
